import { BottleIcon } from "../components/BottleIcon";
import { BrandMark } from "../components/BrandMark";
import { SAUCES, SAUCE_TYPES, type SauceType } from "../lib/types";

interface Props {
  ratedSauces: Set<SauceType>;
  onPick: (s: SauceType) => void;
}

export function SauceSelectScreen({ ratedSauces, onPick }: Props) {
  return (
    <main className="shell flex flex-col px-6 pb-8 pt-10">
      <header className="mb-6 flex justify-center">
        <BrandMark className="h-14 w-auto" />
      </header>

      <div className="mb-8">
        <p className="mb-2 text-xs uppercase tracking-[0.3em] text-amber">The lineup</p>
        <h1 className="text-3xl text-cream sm:text-4xl">Which sauce are you tasting?</h1>
        <p className="mt-3 text-cream-dim">
          Tap the bottle in your hand. You can come back and rate the rest.
        </p>
      </div>

      <div className="grid flex-1 grid-cols-2 gap-3 content-start">
        {SAUCE_TYPES.map((slug) => (
          <SauceCard key={slug} slug={slug} rated={ratedSauces.has(slug)} onPick={onPick} />
        ))}
      </div>
    </main>
  );
}

function SauceCard({
  slug,
  rated,
  onPick,
}: {
  slug: SauceType;
  rated: boolean;
  onPick: (s: SauceType) => void;
}) {
  const meta = SAUCES[slug];
  return (
    <button
      type="button"
      onClick={() => onPick(slug)}
      className="group relative flex aspect-[3/4] flex-col items-stretch overflow-hidden rounded-2xl border border-cream/10 bg-charcoal-2 p-4 text-left transition-transform active:scale-[0.98]"
      style={{
        backgroundImage: `radial-gradient(circle at 50% 110%, ${meta.accent}55, transparent 65%)`,
      }}
      aria-label={`Rate ${meta.name}${rated ? " (already rated)" : ""}`}
    >
      <BottleIcon
        color={meta.accent}
        label={meta.bottleLabel}
        className="mx-auto mb-2 h-28 w-auto drop-shadow-[0_8px_14px_rgba(0,0,0,0.5)]"
      />
      <div className="mt-auto">
        <p className="text-xs uppercase tracking-[0.25em] text-cream-dim">Sauce</p>
        <h2 className="mt-1 font-display text-2xl leading-tight text-cream">{meta.name}</h2>
        <p className="mt-1 line-clamp-2 text-xs text-cream-dim/80">{meta.blurb}</p>
      </div>

      {rated && (
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-amber/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-charcoal">
          <svg
            viewBox="0 0 24 24"
            className="h-3 w-3"
            fill="none"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12.5 10 17.5 19 7" />
          </svg>
          Rated
        </span>
      )}
    </button>
  );
}
